import { useEffect, useRef, useState } from "react";
import { WifiOff, RefreshCw, Wifi, Signal } from "lucide-react";
import { useRouter } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useConnectivity } from "@/app-bridge/hooks/useConnectivity";
import { verifyConnection } from "@/app-bridge/core/connectivity";

/**
 * Visar en tydlig rad högst upp när uppkopplingen sviktar, med nedräkning till
 * nästa automatiska försök och en knapp för att prova direkt. När nätet kommer
 * tillbaka hämtas sidans data om och en kort grön bekräftelse visas.
 */
export function OfflineBanner() {
  const { state, attempts, nextRetryAt, networkType } = useConnectivity();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [countdown, setCountdown] = useState(0);
  const [recovered, setRecovered] = useState(false);
  const [busy, setBusy] = useState(false);
  const wasDown = useRef(false);

  // Nedräkning till nästa automatiska försök.
  useEffect(() => {
    if (state === "online" || !nextRetryAt) {
      setCountdown(0);
      return;
    }
    const tick = () => setCountdown(Math.max(0, Math.ceil((nextRetryAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [state, nextRetryAt]);

  // Återhämtning: hämta om allt som visas när vi är uppkopplade igen.
  useEffect(() => {
    if (state !== "online") {
      wasDown.current = true;
      return;
    }
    if (!wasDown.current) return;
    wasDown.current = false;
    setRecovered(true);
    void queryClient.invalidateQueries();
    void router.invalidate();
    const id = setTimeout(() => setRecovered(false), 3000);
    return () => clearTimeout(id);
  }, [state, queryClient, router]);

  // Tappad uppkoppling ska kännas även utan att man tittar på skärmen.
  useEffect(() => {
    if (state === "offline" && attempts === 2) {
      void import("@/app-bridge/core/wrapper").then((m) => m.hapticSignal("error"));
    }
  }, [state, attempts]);

  if (state === "online" && !recovered) return null;

  if (recovered) {
    return (
      <Bar tone="ok">
        <Wifi className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>Uppkopplad igen — innehållet uppdateras.</span>
      </Bar>
    );
  }

  const retryNow = async () => {
    setBusy(true);
    try {
      await verifyConnection();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Bar tone="warn">
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        {state === "reconnecting"
          ? "Uppkopplingen tappades — försöker återansluta"
          : "Du är offline — du ser sparat innehåll"}
        {countdown > 0 && <span className="opacity-80"> · nytt försök om {countdown} s</span>}
        {networkType && (
          <span className="ml-1 hidden opacity-80 sm:inline">
            <Signal className="mr-0.5 inline h-3 w-3" aria-hidden="true" />
            {networkType === "wifi" ? "Wi-Fi" : networkType === "cellular" ? "Mobilnät" : networkType}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => void retryNow()}
        disabled={busy}
        className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-md bg-amber-950/10 px-2 py-1 text-xs font-semibold transition hover:bg-amber-950/20 disabled:opacity-60"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} aria-hidden="true" />
        Försök nu
      </button>
    </Bar>
  );
}

function Bar({ tone, children }: { tone: "warn" | "ok"; children: React.ReactNode }) {
  return (
    <div
      role="status"
      data-testid="offline-banner"
      aria-live="polite"
      className={`sticky top-0 z-[70] flex items-center justify-center gap-2 px-4 py-2 text-center text-sm font-medium [padding-top:calc(0.5rem+env(safe-area-inset-top))] ${
        tone === "ok" ? "bg-emerald-500 text-emerald-950" : "bg-amber-500 text-amber-950"
      }`}
    >
      {children}
    </div>
  );
}
